/**
 * Pending order files — IndexedDB helper.
 *
 * Why a separate store? sessionStorage only handles strings, so the order
 * wizard's `File` blobs (original tuning file, customer-note PDF) couldn't
 * survive the navigation through the recharge flow. We persist them in IDB
 * keyed by a stable slot name; the wizard reads them back during restore.
 *
 *   await savePendingFile('uploadedFile', fileBlob);
 *   const file = await readPendingFile('uploadedFile');
 *   await clearPendingFiles();
 */
const DB_NAME = 'auto-remap-pending';
const STORE   = 'files';
const VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function savePendingFile(key, blob) {
  if (!blob) return;
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(blob, key);
    tx.oncomplete = () => { db.close(); resolve(true); };
    tx.onerror    = () => { db.close(); reject(tx.error); };
  });
}

export async function readPendingFile(key) {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => { db.close(); resolve(req.result || null); };
      req.onerror   = () => { db.close(); reject(req.error); };
    });
  } catch (e) {
    return null;
  }
}

export async function clearPendingFiles() {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => { db.close(); resolve(true); };
      tx.onerror    = () => { db.close(); reject(tx.error); };
    });
  } catch (e) {
    return false;
  }
}
